// Der Abschnitt "Stichworte" des Kontaktdetails -- aus kontakt.tsx herausgeloest
// (Party 360, erster Schnitt; Verhalten unveraendert).

import {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  Option,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle1,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ASSIGN_UNREACHABLE_MESSAGE,
  END_UNREACHABLE_MESSAGE,
  assignClassification,
  assignedAnnouncement,
  cardinalityHints,
  endClassification,
  endedAnnouncement,
  fetchAssignableTerms,
  fetchClassifications,
  NO_VOCABULARY_MESSAGE,
  removalConfirmation,
  truncationNotice,
  sourceLabel as classificationSourceLabel,
  TERM_UNKNOWN_MESSAGE,
  ALREADY_ENDED_MESSAGE,
  assignmentHint,
  KONFLIKTE_PFAD,
  KONFLIKTE_SEITE,
  validationMessageFor as classificationValidationMessageFor,
  type AssignableTermView,
  type ClassificationView,
  type EndedClassificationView,
} from '../../classifications.ts';
import type { PlainMessage } from '../../access.ts';
import { alsEintrag, searchAssignableEntries } from '../../stichwort-quelle.ts';
import { formatInstant } from '../../parties.ts';
import { brauchtSuchfeld, StichwortSuchfeld, type StichwortEintrag, type StichwortTreffer } from '../../suche/stichwort-suchfeld.tsx';
import { AppLink } from '../../shell/router.tsx';
import { useScreenStyles } from '../../shell/screen-styles.ts';
import { useKontaktStyles } from './stile.ts';
import { CLASSIFICATION_INPUT_ID, CLASSIFICATION_STATUS_ID } from './ids.ts';
import { beiEscapeSchliessen, PlusSymbol } from './plus-symbol.tsx';

const FORMULAR_ID = 'kontakt-stichwort-formular';

/** Kreuz im 24er-Raster; stumm, der Name steht am Knopf. */
function KreuzSymbol(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M7 7l10 10M17 7 7 17" />
    </svg>
  );
}
import type { KontaktScreenProps } from './typen.ts';
import { NebenKnopf } from '../../shell/nebenknopf.tsx';
import { meldungsIntent } from '../../shell/use-section-commands.ts';

/**
 * Der Abschnitt "Stichworte".
 *
 * AUFGABE IN NUTZERSPRACHE: "Ich moechte festhalten, wofuer sich dieser
 * Kontakt interessiert." Zwei Interaktionen ab dem geoeffneten Kontakt:
 * auswaehlen, vergeben (Story-Akzeptanz 2).
 */
export function Stichworte({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  eingebettet = false,
  nurLesen = false,
  onNichtBeschreibbar,
}: KontaktScreenProps & {
  /**
   * Party 360, erster Schnitt: im aufklappbaren Bereich traegt der
   * Accordion-Kopf (h3, id "stichworte") die Ueberschrift; der Abschnitt setzt dann
   * keine eigene, und aria-labelledby zeigt auf den Kopf.
   */
  readonly eingebettet?: boolean;
  /** Der Kontakt ist nicht beschreibbar: dieser Abschnitt zeigt nur noch an (Server: 409 party_not_writable). */
  readonly nurLesen?: boolean;
  /** Der Server lehnte mit 409 party_not_writable ab: die Seite liest den Kontakt neu, damit die Leiste den Zustand zeigt. */
  readonly onNichtBeschreibbar?: () => void;
}): ReactElement {
  const styles = useScreenStyles();
  const own = useKontaktStyles();
  const [zuordnungen, setZuordnungen] = useState<readonly ClassificationView[]>([]);
  const [beendete, setBeendete] = useState<readonly EndedClassificationView[]>([]);
  const [weitere, setWeitere] = useState(0);
  const [historieGekuerzt, setHistorieGekuerzt] = useState(false);
  const [auswahl, setAuswahl] = useState<readonly AssignableTermView[]>([]);
  /**
   * Ob die Begriffsliste GELADEN oder ABGELEHNT wurde. Ohne diese Trennung
   * waere eine abgelehnte Liste von einem leeren Vokabular nicht zu
   * unterscheiden -- und der Bildschirm behauptete einen Zustand, den er
   * nicht gemessen hat.
   */
  const [auswahlStand, setAuswahlStand] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  /** Ob der Server mehr Begriffe hat, als er geschickt hat (ADR-0053 Ziffer 9). */
  const [auswahlGekuerzt, setAuswahlGekuerzt] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string | undefined>(undefined);
  /** Ein per Suche gewaehlter Begriff steht nicht in der ersten Seite: sein Eintrag reist mit. */
  const [gesucht, setGesucht] = useState<StichwortEintrag | undefined>(undefined);
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  // Ob die Meldung einen offenen Ausgang nennt; gesetzt dort, wo der Satz gewaehlt wird.
  const [offen, setOffen] = useState(false);
  const [ladefehler, setLadefehler] = useState<PlainMessage | undefined>(undefined);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  const [feldfehler, setFeldfehler] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  /** Die Zuordnung, deren Entfernen gerade bestaetigt wird -- oder keine. */
  const [zuEntfernen, setZuEntfernen] = useState<ClassificationView | undefined>(undefined);
  const statusRef = useRef<HTMLDivElement | null>(null);
  /** Die Auswahl steht erst nach "+ Stichwort" da; der Fokus folgt ihr, sobald sie eingehaengt ist. */
  const [formularOffen, setFormularOffen] = useState(false);
  const [formularFokus, setFormularFokus] = useState(0);
  useEffect(() => {
    if (formularFokus > 0) document.getElementById(CLASSIFICATION_INPUT_ID)?.focus();
  }, [formularFokus]);
  // Esc schliesst wie in der Zugehoerigkeit, verwirft Auswahl und Fehler und gibt
  // den Fokus an "+ Stichwort" zurueck (APG Disclosure).
  const plusRef = useRef<HTMLButtonElement | null>(null);
  const formularSchliessen = (): void => {
    setFormularOffen(false);
    setGewaehlt(undefined);
    setFeldfehler(undefined);
    window.setTimeout(() => plusRef.current?.focus(), 0);
  };
  const [fokusAnfordern, setFokusAnfordern] = useState(0);
  const laufRef = useRef(0);

  useEffect(() => {
    if (fokusAnfordern > 0) {
      statusRef.current?.focus();
    }
  }, [fokusAnfordern]);

  /** `true`, wenn der Stand geladen ist; sonst steht der Ladefehler da. */
  const laden = useCallback(async (): Promise<boolean> => {
    const lauf = laufRef.current + 1;
    laufRef.current = lauf;
    const gueltig = (): boolean => laufRef.current === lauf;
    setStatus('laedt');
    // DER GANZE ABRUF IM try: bricht er auf TRANSPORTEBENE ab (Dienst nicht
    // erreichbar, DNS, abgebrochene Verbindung), wirft `Promise.all` -- und
    // ohne diesen Zweig bliebe der Abschnitt fuer immer im Ladezustand, ohne
    // den Wiederholweg, den jede HTTP-Ablehnung schon hat. Der Fall ist nicht
    // theoretisch: er ist der Normalfall eines Laptops, der den Netzwechsel
    // verliert (Codex-Cloud-Review #254, P2). Muster von `Verlauf`.
    try {
      const token = await accessToken();
      if (!gueltig()) return false;
      if (token === undefined) {
        onReauthenticate();
        return false;
      }
      // Beide Abrufe zusammen: die Auswahl entscheidet, ob die Hauptaktion
      // ueberhaupt moeglich ist, und die Liste zeigt den Stand.
      const [stand, begriffe] = await Promise.all([
        fetchClassifications({ baseUrl: apiBaseUrl, accessToken: token, partyId }),
        fetchAssignableTerms({ baseUrl: apiBaseUrl, accessToken: token }),
      ]);
      if (!gueltig()) return false;
      if (stand.outcome === 'not-found') {
        // Der Bildschirm selbst hat den Kontakt geladen; ist er hier weg, ist
        // er entzogen worden, waehrend die Seite offen war.
        setStatus('abgelehnt');
        setLadefehler(undefined);
        setOffen(false);
        setMessage(undefined);
        onReauthenticate();
        return false;
      }
      if (stand.outcome === 'rejected') {
        setStatus('abgelehnt');
        setLadefehler(stand.message);
        return false;
      }
      setZuordnungen(stand.classifications);
      setBeendete(stand.endedClassifications);
      setWeitere(stand.additionalClassificationCount);
      setHistorieGekuerzt(stand.endedClassificationsTruncated);
      // EIN ABGELEHNTER ABRUF IST KEIN LEERZUSTAND. Vorher wurde die Auswahl
      // bei jeder Ablehnung auf [] gesetzt -- und weil der Leerzustand an der
      // leeren Auswahl haengt, behauptete der Bildschirm dann "Noch keine
      // Stichworte eingerichtet", obwohl in Wahrheit ein Fehler vorlag. Das
      // ist genau die falsche Aussage, gegen die dieser Leerzustand gebaut
      // ist (Codex-Cloud-Review #254, P2). `auswahlStand` trennt die beiden
      // Faelle, und nur `geladen` mit null Begriffen ist der Leerzustand.
      setAuswahl(begriffe.outcome === 'loaded' ? begriffe.terms : []);
      setAuswahlStand(begriffe.outcome === 'loaded' ? 'geladen' : 'abgelehnt');
      setAuswahlGekuerzt(begriffe.outcome === 'loaded' && begriffe.truncated);
      setLadefehler(begriffe.outcome === 'rejected' ? begriffe.message : undefined);
      setStatus('geladen');
      return true;
    } catch {
      if (!gueltig()) return false;
      setAuswahlStand('abgelehnt');
      setLadefehler({
        headline: 'Die Stichworte sind gerade nicht erreichbar.',
        explanation: 'Es kam keine Antwort.',
        nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
        retryable: true,
      });
      setStatus('abgelehnt');
      return false;
    }
  }, [apiBaseUrl, accessToken, partyId, onReauthenticate]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Ab 21 vergebbaren Begriffen (oder gekuerzter erster Seite) sucht das Feld beim Server (DESIGN 15.10.).
  const mitSuchfeld = auswahlStand === 'geladen' && brauchtSuchfeld(auswahl.length, auswahlGekuerzt);
  const stichwortSuchen = useCallback(
    (getippt: string): Promise<StichwortTreffer> =>
      searchAssignableEntries({ baseUrl: apiBaseUrl, accessToken, onReauthenticate, query: getippt, ersteSeite: auswahl, ersteSeiteGekuerzt: auswahlGekuerzt }),
    [apiBaseUrl, accessToken, onReauthenticate, auswahl, auswahlGekuerzt],
  );
  const gewaehltEintrag: StichwortEintrag | undefined = useMemo(() => {
    if (gewaehlt === undefined) return undefined;
    const geladen = auswahl.find((begriff) => begriff.taxonomyTermId === gewaehlt);
    return geladen === undefined ? (gesucht?.id === gewaehlt ? gesucht : undefined) : alsEintrag(geladen);
  }, [gewaehlt, auswahl, gesucht]);

  const vergeben = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (gewaehlt === undefined) {
      // Die freundlichere Kontrolle zuerst, nie die einzige: der Server
      // antwortet auf denselben Fall mit 422 und Feldpfad.
      setFeldfehler('Wähle zuerst ein Stichwort aus.');
      return;
    }
    setBusy(true);
    setOffen(false);
    setMessage(undefined);
    setBestaetigung(undefined);
    const name = gewaehltEintrag?.name ?? '';
    let ergebnis;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      ergebnis = await assignClassification({ baseUrl: apiBaseUrl, accessToken: token, partyId, taxonomyTermId: gewaehlt });
    } catch {
      // Ohne Antwort bliebe der Abschnitt sonst dauerhaft belegt. Die Auswahl
      // bleibt stehen; die nachgeladene Liste zeigt, ob es angekommen ist.
      // Scheitert auch das Nachladen, spricht allein der Ladefehler -- der
      // Satz "die Liste zeigt den Stand" stimmte dann nicht.
      setOffen(true);
      setMessage((await laden()) ? ASSIGN_UNREACHABLE_MESSAGE : undefined);
      setFokusAnfordern((zahl) => zahl + 1);
      return;
    } finally {
      setBusy(false);
    }
    if (ergebnis.outcome === 'assigned') {
      setGewaehlt(undefined);
      setBestaetigung(assignedAnnouncement(name));
      setFokusAnfordern((zahl) => zahl + 1);
      await laden();
      return;
    }
    if (ergebnis.outcome === 'invalid') {
      const erster = ergebnis.errors[0];
      setFeldfehler(erster === undefined ? undefined : classificationValidationMessageFor(erster.fieldPath, erster.reasonCode));
      return;
    }
    if (ergebnis.outcome === 'not-found') {
      // "Der Kontakt ist weg" und "das Stichwort ist weg" sind zwei
      // verschiedene naechste Schritte -- deshalb unterscheidet die Antwort
      // sie, obwohl der Server in beiden Faellen 404 sagt (CM-SEC-003 gilt
      // fuer die Kennung, nicht fuer den Rueckweg).
      setOffen(false);
      setMessage(ergebnis.what === 'term' ? TERM_UNKNOWN_MESSAGE : undefined);
      if (ergebnis.what === 'party') {
        setStatus('abgelehnt');
      } else {
        // Der Begriff ist weg: als Chip stehen bleiben darf er nicht, sonst
        // ließe er sich erneut absenden, obwohl die Meldung auf die Auswahl verweist.
        setGewaehlt(undefined);
        setGesucht(undefined);
        await laden();
      }
      setFokusAnfordern((zahl) => zahl + 1);
      return;
    }
    setOffen(false);
    setMessage(ergebnis.message);
    setFokusAnfordern((zahl) => zahl + 1);
    if (ergebnis.reasonCode === 'party_not_writable') onNichtBeschreibbar?.();
    if (ergebnis.reasonCode === 'classification_duplicate') {
      // Die Zuordnung IST da -- der Stand wird nachgeladen, damit die Liste
      // zeigt, was die Meldung sagt.
      setGewaehlt(undefined);
      await laden();
    }
  };

  const entfernen = async (zuordnung: ClassificationView): Promise<void> => {
    setZuEntfernen(undefined);
    setBusy(true);
    setOffen(false);
    setMessage(undefined);
    setBestaetigung(undefined);
    let ergebnis;
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      ergebnis = await endClassification({
        baseUrl: apiBaseUrl,
        accessToken: token,
        partyId,
        classificationAssignmentId: zuordnung.classificationAssignmentId,
      });
    } catch {
      setOffen(true);
      setMessage((await laden()) ? END_UNREACHABLE_MESSAGE : undefined);
      setFokusAnfordern((zahl) => zahl + 1);
      return;
    } finally {
      setBusy(false);
    }
    setFokusAnfordern((zahl) => zahl + 1);
    if (ergebnis.outcome === 'ended') {
      setBestaetigung(endedAnnouncement(zuordnung.displayName));
      await laden();
      return;
    }
    if (ergebnis.outcome === 'not-found') {
      await laden();
      return;
    }
    setOffen(false);
    setMessage(ergebnis.message);
    if (ergebnis.reasonCode === 'party_not_writable') onNichtBeschreibbar?.();
    const geladen = await laden();
    // "Bereits entfernt" sagt "Die Liste ist neu geladen." -- scheitert das
    // Nachladen, spricht allein der Ladefehler.
    if (!geladen && ergebnis.message === ALREADY_ENDED_MESSAGE) setMessage(undefined);
  };

  const hinweise = cardinalityHints(zuordnungen);
  // NUR eine erfolgreich geladene, leere Liste ist der Leerzustand.
  const keinVokabular = status === 'geladen' && auswahlStand === 'geladen' && auswahl.length === 0;

  // "+ Stichwort" am Ende der Ueberschrift wie "+ Rolle" und "+ Beziehung" (DESIGN
  // 13.10., M-11); oeffnet die Auswahl erst auf Anforderung (5.2.4), der Name
  // sagt, was geschieht (WCAG 2.5.3).
  const plusKnopf =
    nurLesen || keinVokabular || status !== 'geladen' ? undefined : (
      <Button
        ref={(knoten: HTMLButtonElement | null) => {
          plusRef.current = knoten;
        }}
        appearance="subtle"
        size="small"
        className={own.abschnittPlus}
        icon={<PlusSymbol />}
        aria-label="Stichwort hinzufügen"
        aria-expanded={formularOffen}
        aria-controls={formularOffen ? FORMULAR_ID : undefined}
        onClick={() => {
          if (formularOffen) {
            formularSchliessen();
            return;
          }
          setFormularOffen(true);
          setFormularFokus((zahl) => zahl + 1);
        }}
      >
        Stichwort
      </Button>
    );
  const erklaerung = 'Stichworte sind in der ganzen Gruppe gleich und helfen dabei, später die richtigen Kontakte zusammenzustellen.';

  return (
    <section className={own.gruppe} aria-labelledby="stichworte">
      {eingebettet ? (
        plusKnopf
      ) : (
        <div className={own.abschnittKopf} data-abschnittkopf="">
          <Subtitle1 as="h2" id="stichworte">
            Stichworte
          </Subtitle1>
          {plusKnopf}
        </div>
      )}
      {/* Die ZUSAMMENFASSUNG steht AUSSERHALB der Statusregion. In die Region
          kommen nur ERGEBNISSE einer Handlung; ein Dauertext darin waere bei
          jedem Rendern ein Kandidat fuer eine Ansage, die niemand ausgeloest
          hat -- und er machte die Region dauerhaft belegt, wo die Zusage
          lautet, dass immer nur eine Region etwas zu sagen hat. */}
      {/* Die Marken zaehlen sich selbst (die Liste nennt die Zahl); ein Satz steht
          nur, wenn er etwas sagt, was man nicht sieht (M-11). */}
      {status === 'geladen' && !keinVokabular && (zuordnungen.length === 0 || weitere > 0) ? (
        <Caption1 className={own.hinweis}>
          {zuordnungen.length === 0 ? 'Noch keine Stichworte vergeben.' : `${weitere} weitere nicht angezeigt.`}
        </Caption1>
      ) : undefined}
      {status === 'geladen' && zuordnungen.length > 0 ? (
        <ul className={own.stichwortReihe} aria-label="Vergebene Stichworte">
          {zuordnungen.map((zuordnung) => {
            // v4 tag: die Marken in EINER Reihe; die Herkunft (5.2 Nr. 5) steht im
            // Tooltip fuer die Maus und als vorgelesener Text in der Marke, damit
            // sie nicht je Marke eine Zeile kostet (DESIGN-P3 #502, M-11/D-11).
            const herkunft = `${zuordnung.taxonomyDisplayName}, ${classificationSourceLabel(zuordnung.sourceType)}, ${formatInstant(zuordnung.validFrom)}`;
            const hinweis = assignmentHint(zuordnung);
            return (
              <li key={zuordnung.classificationAssignmentId} className={own.stichwortZeile}>
                <Tooltip content={hinweis === undefined ? herkunft : `${herkunft}. ${hinweis}`} relationship="inaccessible" withArrow>
                  {/* Ohne Schreibrecht fehlt das Kreuz: dann ist die Marke selbst
                      fokussierbar, sonst erreichte die Tastatur die Herkunft nie
                      (Codex-Cloud-P1 #573). Ihr Text traegt Name und Herkunft. */}
                  <span
                    className={mergeClasses(own.stichwortMarke, hinweis !== undefined && own.stichwortMarkeHinweis)}
                    tabIndex={nurLesen ? 0 : undefined}
                  >
                    <Body1 className={own.text}>{zuordnung.displayName}</Body1>
                    <span className={own.nurVorgelesen}>
                      {`, ${herkunft}`}
                      {hinweis === undefined ? '' : `. ${hinweis}`}
                    </span>
                    {nurLesen ? undefined : (
                      <Button
                        appearance="transparent"
                        size="small"
                        className={own.stichwortWeg}
                        disabled={busy}
                        icon={<KreuzSymbol />}
                        onClick={() => setZuEntfernen(zuordnung)}
                        aria-label={`Stichwort ${zuordnung.displayName} entfernen`}
                      />
                    )}
                  </span>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      ) : undefined}
      {/* Was Stichworte sind: im Leerzustand und ohne Schreibrecht als Beschreibung
          (B73), sonst im Formular, das "+ Stichwort" oeffnet (M-11). */}
      {nurLesen || (status === 'geladen' && zuordnungen.length === 0) ? <Caption1 className={own.hinweis}>{erklaerung}</Caption1> : undefined}

      {/* Der wahre Leerzustand: keine Auswahl, keine abgeblendete Attrappe,
          sondern ein Satz mit Grund und naechstem Schritt (ADR-0002). */}
      {nurLesen && status === 'geladen' && zuordnungen.length === 0 ? (
        <Body1>Keine Stichworte vergeben.</Body1>
      ) : keinVokabular ? (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>{NO_VOCABULARY_MESSAGE.headline}</MessageBarTitle>
            <div>{NO_VOCABULARY_MESSAGE.explanation}</div>
            <div>{NO_VOCABULARY_MESSAGE.nextStep}</div>
          </MessageBarBody>
        </MessageBar>
      ) : nurLesen || !formularOffen ? undefined : (
        <form id={FORMULAR_ID} className={own.wahl} noValidate onSubmit={(event) => void vergeben(event)} onKeyDown={(event) => beiEscapeSchliessen(event, formularSchliessen)}>
          <Caption1 className={own.hinweis}>{zuordnungen.length > 0 ? `Halte fest, wofür sich dieser Kontakt interessiert. ${erklaerung}` : 'Halte fest, wofür sich dieser Kontakt interessiert.'}</Caption1>
          {mitSuchfeld ? (
            <>
              <StichwortSuchfeld
                label="Stichwort"
                hinweis="Angeboten werden die Stichworte, die gerade vergeben werden können."
                eingabeId={CLASSIFICATION_INPUT_ID}
                fehler={feldfehler}
                einzeln
                gesperrt={busy || status !== 'geladen'}
                ersteSeite={auswahl.map(alsEintrag)}
                ersteSeiteGekuerzt={auswahlGekuerzt}
                suchen={stichwortSuchen}
                gewaehlt={gewaehltEintrag === undefined ? [] : [gewaehltEintrag]}
                onHinzufuegen={(eintrag) => {
                  setGewaehlt(eintrag.id);
                  setGesucht(eintrag);
                  if (feldfehler !== undefined) setFeldfehler(undefined);
                  return true;
                }}
                onEntfernen={() => {
                  setGewaehlt(undefined);
                  setGesucht(undefined);
                }}
              />
              {/* Die Definition trennt gleichnamige Stichworte; das Suchfeld zeigt nur Name und Liste. */}
              {gewaehltEintrag?.beschreibung ? (
                <Caption1>
                  „{gewaehltEintrag.name}“: {gewaehltEintrag.beschreibung}
                </Caption1>
              ) : undefined}
            </>
          ) : (
          <Field
            label="Stichwort"
            hint="Die Auswahl zeigt den Begriff und wofür er steht."
            validationMessage={feldfehler ?? null}
            validationState={feldfehler === undefined ? 'none' : 'error'}
          >
            {/* Die Kuerzung steht VOR der Auswahl und nicht darunter: wer
                sein Stichwort nicht findet, soll den Grund lesen, bevor er
                die Liste dreimal durchgeht. */}
            {auswahlGekuerzt ? <Caption1>{truncationNotice(auswahl.length)}</Caption1> : undefined}
            <Dropdown
              id={CLASSIFICATION_INPUT_ID}
              disabled={busy || status !== 'geladen'}
              placeholder="Stichwort wählen"
              value={auswahl.find((begriff) => begriff.taxonomyTermId === gewaehlt)?.displayName ?? ''}
              selectedOptions={gewaehlt === undefined ? [] : [gewaehlt]}
              onOptionSelect={(_, data) => {
                setGewaehlt(data.optionValue);
                if (feldfehler !== undefined) setFeldfehler(undefined);
              }}
            >
              {auswahl.map((begriff) => (
                <Option key={begriff.taxonomyTermId} value={begriff.taxonomyTermId} text={begriff.displayName}>
                  {/* Begriff UND Definition: die Vollerhebung nennt die
                      fehlende Definition als Ursache der Doppelanlagen, und
                      wer waehlen soll, muss wissen, wofuer ein Wort steht. */}
                  <div>
                    <Body1>{begriff.displayName}</Body1>
                    <Caption1>{begriff.definition}</Caption1>
                  </div>
                </Option>
              ))}
            </Dropdown>
          </Field>
          )}
          <div className={styles.actions}>
            {/* secondary UND NICHT primary -- bitte nicht "korrigieren":
                NFR-UX-004 meint EINE Hauptaktion je BILDSCHIRM, und die ist
                "Notiz festhalten" (Kopf dieser Datei). Zwei primaere
                Schaltflaechen waeren nicht zwei Hauptaktionen, sondern keine.
                Dies hier ist die Hauptaktion SEINES Abschnitts, weil sie die
                einzige Aktion darin ist. Bestaetigt vom Dirigenten am
                17.09.2026; Begruendung im Pruefprotokoll Stichworte,
                Abschnitt 4. */}
            <Button appearance="secondary" type="submit" disabled={busy || status !== 'geladen'}>
              Stichwort vergeben
            </Button>
          </div>
        </form>
      )}

      {/* Der LADEZUSTAND steht AUSSERHALB der Statusregion, und das ist die
          billigste Abhilfe gegen die einzige Lage, die dieser Abschnitt nicht
          vom Verlauf erbt: der Verlauf entsteht erst, wenn der Rahmen geladen
          HAT -- dieser Abschnitt laedt selbst und haengt parallel. Spraeche
          seine Region waehrend des eigenen Ladens, koennten zwei Regionen
          gleichzeitig Text tragen. In die Region kommen deshalb nur
          ERGEBNISSE (Vorschlag der ST-ACT-001-Session, 17.09.2026). */}
      {status === 'laedt' ? <Spinner labelPosition="after" label="Stichworte werden geladen" /> : undefined}

      {/* aria-label, weil kurze Saetze ihren Abschnitt nicht tragen: "Stichwort
          vergeben." allein sagt einer Screenreader-Nutzerin nicht, WO sie ist,
          und mit der dritten Region auf diesem Bildschirm wird die
          Unterscheidbarkeit knapp. Muster von der ACT-Session, 17.09.2026:
          "Status: Kontakt" / "Status: Verlauf" / "Status: Stichworte". */}
      <div
        aria-live="polite"
        role="status"
        aria-label="Status: Stichworte"
        id={CLASSIFICATION_STATUS_ID}
        ref={statusRef}
        tabIndex={-1}
        className={own.hinweis}
      >
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Gespeichert</MessageBarTitle>
              <div>{bestaetigung}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {message !== undefined ? (
          <MessageBar intent={meldungsIntent(message, offen)}>
            <MessageBarBody>
              <MessageBarTitle>{message.headline}</MessageBarTitle>
              <div>{message.explanation}</div>
              <div>{message.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {ladefehler !== undefined ? (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{ladefehler.headline}</MessageBarTitle>
              <div>{ladefehler.explanation}</div>
              <div>{ladefehler.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>

      {/* Der Datenqualitaetshinweis: in WORTEN und nicht allein in Farbe
          (NFR-ACC-001), und er weist nichts ab (ADR-0030 Punkt 4). Er steht
          AUSSERHALB der Statusregion, weil er den BESTAND beschreibt und nicht
          das Ergebnis einer Handlung -- in der Region waere er eine Ansage bei
          jedem Laden. */}
      {status === 'geladen'
        ? hinweise.map((hinweis) => (
            <MessageBar key={hinweis} intent="info">
              <MessageBarBody>
                <div>
                  {hinweis.endsWith(`${KONFLIKTE_SEITE}.`) ? (
                    <>
                      {hinweis.slice(0, -`${KONFLIKTE_SEITE}.`.length)}
                      <AppLink to={KONFLIKTE_PFAD}>{KONFLIKTE_SEITE}</AppLink>.
                    </>
                  ) : (
                    hinweis
                  )}
                </div>
              </MessageBarBody>
            </MessageBar>
          ))
        : undefined}

      {/* Progressive Offenlegung: die Historie liegt zugeklappt und ist per
          Tastatur erreichbar (5.2 Nr. 9). */}
      {status === 'geladen' && beendete.length > 0 ? (
        <Accordion collapsible>
          <AccordionItem value="entfernte-stichworte">
            <AccordionHeader>Früher vergebene Stichworte ({beendete.length}{historieGekuerzt ? '+' : ''})</AccordionHeader>
            <AccordionPanel>
              <ul className={own.liste} aria-label="Früher vergebene Stichworte">
                {beendete.map((zuordnung) => (
                  <li key={zuordnung.classificationAssignmentId} className={own.eintrag}>
                    <Body1 className={own.text}>{zuordnung.displayName}</Body1>
                    <Caption1>
                      {zuordnung.taxonomyDisplayName}, {classificationSourceLabel(zuordnung.sourceType)}, entfernt am{' '}
                      {formatInstant(zuordnung.endedAt)}
                    </Caption1>
                  </li>
                ))}
              </ul>
              {historieGekuerzt ? (
                <Caption1>Ältere Einträge sind hier nicht aufgeführt.</Caption1>
              ) : undefined}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ) : undefined}

      {/* Die folgenbezogene Bestaetigung (NFR-UX-005, CM-UX-004): sie nennt
          die Wirkung UND den Rueckweg, damit die Entscheidung informiert ist
          und nicht bloss gebremst. */}
      {/* Der Dialog wird NUR EINGEHAENGT, wenn er gebraucht wird -- nicht
          dauerhaft mit `open={false}`. Gemessen am 17.09.2026: Fluent setzt
          seine Fokuswaechter (`<i tabindex="0" aria-hidden="true">`) auch im
          geschlossenen Zustand in den Body, und axe meldet das zu Recht als
          `aria-hidden-focus` (serious): ein Element, das der Screenreader
          verbirgt, darf den Tabfokus nicht fangen. Ohne diese Bedingung
          traegt JEDER Kontakt zwei solche Waechter, auch wenn nie jemand ein
          Stichwort entfernt. */}
      {zuEntfernen !== undefined ? (
      <Dialog open onOpenChange={(_, data) => { if (!data.open) setZuEntfernen(undefined); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Stichwort entfernen?</DialogTitle>
            <DialogContent>{zuEntfernen === undefined ? '' : removalConfirmation(zuEntfernen.displayName)}</DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <NebenKnopf>Abbrechen</NebenKnopf>
              </DialogTrigger>
              <Button
                appearance="primary"
                disabled={busy}
                onClick={() => { if (zuEntfernen !== undefined) void entfernen(zuEntfernen); }}
              >
                Entfernen
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      ) : undefined}

      {ladefehler !== undefined && ladefehler.retryable ? (
        <div className={styles.actions}>
          <Button appearance="secondary" disabled={busy} onClick={() => void laden()}>
            Stichworte erneut laden
          </Button>
        </div>
      ) : undefined}
    </section>
  );
}
